import React, { useState, useEffect } from 'react';
import LoginPage from './pages/LoginPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import Dashboard from './pages/Dashboard';
import ScenariosPage from './pages/ScenariosPage';
import BatchResultsPage from './pages/BatchResultsPage';
import VaOverviewPage from './pages/VaOverviewPage';
import SubmissionReviewPage from './pages/SubmissionReviewPage';
import UserManagementPage from './pages/UserManagementPage';
import BatchManagementPage from './pages/BatchManagementPage';
import UploadLogPage from './pages/UploadLogPage';
import OverrideAnalyticsPage from './pages/OverrideAnalyticsPage';
import CostBenefitPage from './pages/CostBenefitPage';
import GuidePage from './pages/GuidePage';
import { getSession, clearSession, getDevSettings, setDevDateOverride, setTestMode, updateSession } from './services/api';

const NAV_STORAGE_KEY = 'acct_nav_state_v1';

function loadNavState() {
  try {
    const raw = localStorage.getItem(NAV_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveNavState(state) {
  try { localStorage.setItem(NAV_STORAGE_KEY, JSON.stringify(state)); } catch {}
}

function clearNavState() {
  try { localStorage.removeItem(NAV_STORAGE_KEY); } catch {}
}

const ADMIN_ONLY_PAGES = new Set(['scenarios', 'batchManagement', 'users', 'uploadLog', 'overrideAnalytics', 'costBenefit']);
// Cost-Benefit Dashboard is restricted to these two portal emails only.
const COST_BENEFIT_EMAILS = new Set(['gregg@hellorache.com', 'nick@hellorache.com']);

const styles = {
  // Viewport-locked shell: the sidebar stays fixed while only the main content
  // scrolls (Monday #12017645508 - sidebar fixed while scrolling).
  app: { height: '100vh', display: 'flex', overflow: 'hidden' },
  sidebar: {
    width: '240px',
    background: '#2d1a2e',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    height: '100vh',
    overflowY: 'auto',
  },
  sidebarHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid #4a2d4a',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  sidebarLogo: {
    height: '32px',
    width: 'auto',
    borderRadius: '4px',
  },
  sidebarTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#e8d5e7',
  },
  navItem: {
    padding: '14px 20px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#c4a3c3',
    borderLeft: '3px solid transparent',
    transition: 'all 0.2s',
  },
  navItemActive: {
    color: '#fff',
    background: '#4a2d4a',
    borderLeft: '3px solid #9C3D97',
  },
  sidebarFooter: {
    marginTop: 'auto',
    padding: '16px 20px',
    borderTop: '1px solid #4a2d4a',
  },
  userName: {
    fontSize: '14px',
    color: '#e8d5e7',
    marginBottom: '4px',
  },
  userRole: {
    fontSize: '12px',
    color: '#9a7a99',
    textTransform: 'uppercase',
    marginBottom: '12px',
  },
  logoutBtn: {
    background: '#4a2d4a',
    border: 'none',
    color: '#c4a3c3',
    padding: '8px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    width: '100%',
  },
  main: {
    flex: 1,
    padding: '24px 32px',
    overflow: 'auto',
  },
};

export default function App() {
  const [session, setSession] = useState(getSession());
  // Rehydrate navigation state from localStorage so the user doesn't lose
  // their place after a browser refresh. Only hydrate if there's an existing
  // session; otherwise start clean on dashboard.
  const initialNav = session ? (loadNavState() || {}) : {};
  const [activePage, setActivePage] = useState(initialNav.activePage || 'dashboard');
  const [selectedSubmission, setSelectedSubmission] = useState(initialNav.selectedSubmission || null);
  const [selectedVa, setSelectedVa] = useState(initialNav.selectedVa || null); // { vaName, batch, submissions }
  const [selectedBatch, setSelectedBatch] = useState(initialNav.selectedBatch || null);
  const [devDateOverride, setDevDateOverrideState] = useState('');
  const [devDateInput, setDevDateInput] = useState('');
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const [applyConfirmInput, setApplyConfirmInput] = useState('');
  const [testMode, setTestModeState] = useState(false);

  async function handleToggleTestMode() {
    const next = !testMode;
    setTestModeState(next);
    try { await setTestMode(next); } catch { setTestModeState(!next); }
  }
  // Sidebar collapse + DEV bar visibility (persisted so the choice sticks).
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try { return localStorage.getItem('acct_sidebar_open') !== 'false'; } catch { return true; }
  });
  const [devBarHidden, setDevBarHidden] = useState(() => {
    try { return localStorage.getItem('acct_devbar_hidden') === 'true'; } catch { return false; }
  });
  function toggleSidebar() {
    setSidebarOpen((v) => { try { localStorage.setItem('acct_sidebar_open', String(!v)); } catch {} return !v; });
  }
  function setDevBarHiddenPersist(hidden) {
    setDevBarHidden(hidden); try { localStorage.setItem('acct_devbar_hidden', String(hidden)); } catch {}
  }

  const isDeveloper = session && session.developer === true;
  const isAdmin = session && session.role === 'admin';
  const canSeeCostBenefit = isAdmin && COST_BENEFIT_EMAILS.has(((session && session.email) || '').trim().toLowerCase());

  useEffect(() => {
    if (isDeveloper) {
      getDevSettings().then((data) => {
        const v = data.test_date_override || '';
        setDevDateOverrideState(v);
        setDevDateInput(v);
        setTestModeState(data.test_mode === true);
      }).catch(() => {});
    }
  }, [isDeveloper]);

  // Guard: if the rehydrated page is admin-only but the user isn't admin,
  // fall back to dashboard. Runs once on mount plus whenever admin status flips.
  useEffect(() => {
    if (!session) return;
    const blockedAdminPage = !isAdmin && ADMIN_ONLY_PAGES.has(activePage);
    const blockedCostBenefit = activePage === 'costBenefit' && !canSeeCostBenefit;
    if (blockedAdminPage || blockedCostBenefit) {
      setActivePage('dashboard');
      setSelectedSubmission(null);
      setSelectedVa(null);
      setSelectedBatch(null);
    }
  }, [session, isAdmin, canSeeCostBenefit, activePage]);

  // Persist nav state to localStorage so a refresh keeps the user on the
  // same page. Data attached to selectedVa (submissions array) may be
  // slightly stale after a refresh; users can navigate back to the batch
  // page to refresh it. Only persist when signed in.
  useEffect(() => {
    if (!session) return;
    saveNavState({ activePage, selectedSubmission, selectedVa, selectedBatch });
  }, [session, activePage, selectedSubmission, selectedVa, selectedBatch]);

  if (!session) {
    return <LoginPage onSuccess={() => {
      // Clear any lingering nav state from a previous user so the new
      // session starts on the dashboard.
      clearNavState();
      setActivePage('dashboard');
      setSelectedSubmission(null);
      setSelectedVa(null);
      setSelectedBatch(null);
      setSession(getSession());
    }} />;
  }

  // First-login gate: when the backend stamps must_change_password=true on a
  // user (initial seed, admin create, admin reset), block all portal access
  // until they replace the temp password with one only they know.
  if (session.must_change_password === true) {
    return (
      <ChangePasswordPage
        email={session.email}
        onSuccess={() => {
          const next = updateSession({ must_change_password: false });
          setSession(next);
        }}
        onSignOut={() => {
          clearNavState();
          clearSession();
          setSession(null);
        }}
      />
    );
  }

  function handleLogout() {
    clearNavState();
    clearSession();
    setSession(null);
  }

  function navTo(page) {
    setActivePage(page);
    setSelectedSubmission(null);
    setSelectedVa(null);
    setSelectedBatch(null);
  }

  // `identity` carries the trainee's stable ids (Monday #12017317679) so the
  // VA Evaluation Overview can refresh and Mark-NS without having to recover
  // an email by scanning the submissions snapshot.
  function handleViewVa(vaName, batch, submissions, gradingComplete, missingGraded, tsName, identity) {
    setSelectedVa({
      vaName,
      batch,
      submissions,
      gradingComplete,
      missingGraded: missingGraded || [],
      tsName: tsName || '',
      cId: (identity && identity.cId) || '',
      vaEmail: (identity && identity.vaEmail) || '',
      traineeKey: (identity && identity.traineeKey) || '',
    });
    setSelectedBatch(batch);
    setActivePage('vaOverview');
  }

  function handleReviewSubmission(submissionId) {
    setSelectedSubmission(submissionId);
    setActivePage('review');
  }

  return (
    <div style={styles.app}>
      {sidebarOpen && (
      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <img src="/hellorache-logo.jpg" alt="HelloRache" style={styles.sidebarLogo} />
          <span style={styles.sidebarTitle}>AI Chart Checking Tool</span>
          <button
            onClick={toggleSidebar}
            title="Hide sidebar"
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#c4a3c3', fontSize: '18px', cursor: 'pointer', lineHeight: 1 }}
          >
            &laquo;
          </button>
        </div>
        <div
          style={{ ...styles.navItem, ...(activePage === 'dashboard' ? styles.navItemActive : {}) }}
          onClick={() => navTo('dashboard')}
        >
          Dashboard
        </div>
        <div
          style={{ ...styles.navItem, ...(activePage === 'batches' ? styles.navItemActive : {}) }}
          onClick={() => navTo('batches')}
        >
          Batch Results
        </div>
        {isAdmin && (
          <div
            style={{ ...styles.navItem, ...(activePage === 'scenarios' ? styles.navItemActive : {}) }}
            onClick={() => navTo('scenarios')}
          >
            Case Management
          </div>
        )}
        {isAdmin && (
          <div
            style={{ ...styles.navItem, ...(activePage === 'batchManagement' ? styles.navItemActive : {}) }}
            onClick={() => navTo('batchManagement')}
          >
            Batch Management
          </div>
        )}
        {isAdmin && (
          <div
            style={{ ...styles.navItem, ...(activePage === 'users' ? styles.navItemActive : {}) }}
            onClick={() => navTo('users')}
          >
            User Management
          </div>
        )}
        {isAdmin && (
          <div
            style={{ ...styles.navItem, ...(activePage === 'uploadLog' ? styles.navItemActive : {}) }}
            onClick={() => navTo('uploadLog')}
          >
            Upload Log
          </div>
        )}
        {isAdmin && (
          <div
            style={{ ...styles.navItem, ...(activePage === 'overrideAnalytics' ? styles.navItemActive : {}) }}
            onClick={() => navTo('overrideAnalytics')}
          >
            Override Analytics
          </div>
        )}
        {canSeeCostBenefit && (
          <div
            style={{ ...styles.navItem, ...(activePage === 'costBenefit' ? styles.navItemActive : {}) }}
            onClick={() => navTo('costBenefit')}
          >
            AI Grading Impact
          </div>
        )}
        <div
          style={{ ...styles.navItem, marginTop: 'auto', borderTop: '1px solid #4a2d4a', ...(activePage === 'guide' ? styles.navItemActive : {}) }}
          onClick={() => navTo('guide')}
        >
          Guide
        </div>
        <div style={{ ...styles.sidebarFooter, marginTop: 0 }}>
          <div style={styles.userName}>{session.name}</div>
          <div style={styles.userRole}>{session.role}</div>
          <button style={styles.logoutBtn} onClick={handleLogout}>Logout</button>
        </div>
      </div>
      )}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        {!sidebarOpen && (
          <button
            onClick={toggleSidebar}
            title="Show sidebar"
            style={{ alignSelf: 'flex-start', margin: '10px 0 0 12px', padding: '6px 12px', background: '#2d1a2e', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', flexShrink: 0 }}
          >
            &#9776; Menu
          </button>
        )}
        {isDeveloper && !devBarHidden && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 32px',
            background: '#fefcbf', borderBottom: '1px solid #ecc94b', fontSize: '13px', color: '#744210',
            flexShrink: 0, flexWrap: 'wrap',
          }}>
            <strong>DEV</strong>
            <span>Date Override:</span>
            <input
              type="date"
              value={devDateInput}
              onChange={(e) => setDevDateInput(e.target.value)}
              style={{ padding: '4px 8px', border: '1px solid #d69e2e', borderRadius: '4px', fontSize: '13px' }}
            />
            <button
              onClick={() => {
                if (!devDateInput) return;
                setApplyConfirmInput('');
                setShowApplyConfirm(true);
              }}
              disabled={!devDateInput}
              style={{ padding: '4px 12px', background: '#d69e2e', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '12px', fontWeight: '600', cursor: devDateInput ? 'pointer' : 'not-allowed', opacity: devDateInput ? 1 : 0.5 }}
            >
              Apply
            </button>
            <button
              onClick={async () => {
                await setDevDateOverride('');
                setDevDateOverrideState('');
                setDevDateInput('');
              }}
              style={{ padding: '4px 12px', background: '#edf2f7', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}
            >
              Clear
            </button>
            {devDateOverride && (
              <span style={{ fontWeight: '600', color: '#c53030' }}>
                Active: {devDateOverride}
              </span>
            )}
            {!devDateOverride && (
              <span style={{ color: '#a0aec0' }}>
                Using real date
              </span>
            )}
            <span style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              borderLeft: '1px solid #d69e2e', paddingLeft: '12px',
              background: testMode ? '#fed7d7' : 'transparent', borderRadius: '4px', padding: testMode ? '2px 8px 2px 12px' : '0 0 0 12px',
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: '700', color: testMode ? '#c53030' : '#744210' }}>
                <input type="checkbox" checked={testMode} onChange={handleToggleTestMode} />
                Test Mode {testMode ? 'ON' : 'OFF'}
              </label>
              <span style={{ fontSize: '12px', color: testMode ? '#c53030' : '#744210' }}>
                {testMode
                  ? '— test submissions are EXCLUDED from AI Grading Impact. Turn OFF when testing is done.'
                  : '— SWITCH ON before any testing so test submissions are not counted in AI Grading Impact.'}
              </span>
            </span>
            <button
              onClick={() => setDevBarHiddenPersist(true)}
              title="Hide this DEV bar"
              style={{ marginLeft: 'auto', padding: '4px 10px', background: 'none', border: '1px solid #d69e2e', borderRadius: '4px', fontSize: '12px', color: '#744210', cursor: 'pointer' }}
            >
              Hide bar &times;
            </button>
          </div>
        )}
        {isDeveloper && devBarHidden && (
          <button
            onClick={() => setDevBarHiddenPersist(false)}
            title="Show the DEV date-override bar"
            style={{ alignSelf: 'flex-start', margin: '6px 0 0 12px', padding: '3px 10px', background: '#fefcbf', border: '1px solid #ecc94b', borderRadius: '4px', fontSize: '12px', color: '#744210', cursor: 'pointer', flexShrink: 0 }}
          >
            Show DEV bar
          </button>
        )}
        <div style={styles.main}>
        {activePage === 'dashboard' && <Dashboard />}
        {activePage === 'batches' && (
          <BatchResultsPage onViewVa={handleViewVa} initialBatch={selectedBatch} />
        )}
        {activePage === 'vaOverview' && selectedVa && (
          <VaOverviewPage
            vaName={selectedVa.vaName}
            batch={selectedVa.batch}
            submissions={selectedVa.submissions}
            gradingComplete={selectedVa.gradingComplete}
            missingGraded={selectedVa.missingGraded}
            tsName={selectedVa.tsName}
            cId={selectedVa.cId}
            vaEmailProp={selectedVa.vaEmail}
            lastReviewedId={selectedSubmission}
            onReview={handleReviewSubmission}
            onBack={() => { setSelectedVa(null); setActivePage('batches'); }}
          />
        )}
        {activePage === 'scenarios' && isAdmin && <ScenariosPage />}
        {activePage === 'batchManagement' && isAdmin && <BatchManagementPage />}
        {activePage === 'users' && isAdmin && <UserManagementPage />}
        {activePage === 'uploadLog' && isAdmin && <UploadLogPage />}
        {activePage === 'overrideAnalytics' && isAdmin && <OverrideAnalyticsPage />}
        {activePage === 'costBenefit' && canSeeCostBenefit && <CostBenefitPage />}
        {activePage === 'guide' && <GuidePage />}
        {activePage === 'review' && selectedSubmission && (
          <SubmissionReviewPage
            submissionId={selectedSubmission}
            onBack={() => selectedVa ? setActivePage('vaOverview') : navTo('batches')}
          />
        )}
        </div>
      </div>
      {showApplyConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 2000,
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '28px',
            width: '460px', maxWidth: '92vw', boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
          }}>
            <div style={{
              background: '#fed7d7', color: '#9b2c2c', padding: '12px 14px',
              borderRadius: '6px', fontSize: '14px', fontWeight: '700',
              marginBottom: '16px', lineHeight: '1.45',
            }}>
              CAUTION: DO NOT APPLY WHILE TRAINING IS ONGOING.
              <div style={{ fontWeight: '600', marginTop: '6px' }}>
                The date override is a global server setting. Every VA and admin will see the system as if today were the date below. Real submission deadlines and training-day gating will use this fake date until it is cleared.
              </div>
            </div>
            <div style={{ fontSize: '14px', color: '#2d3748', marginBottom: '12px' }}>
              Override date: <strong>{devDateInput}</strong>
            </div>
            <div style={{ fontSize: '14px', color: '#2d3748', marginBottom: '6px' }}>
              Type <strong>Apply</strong> to confirm:
            </div>
            <input
              type="text"
              autoFocus
              value={applyConfirmInput}
              onChange={(e) => setApplyConfirmInput(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', border: '2px solid #e2e8f0',
                borderRadius: '6px', fontSize: '14px', outline: 'none', boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '18px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowApplyConfirm(false); setApplyConfirmInput(''); }}
                style={{ padding: '8px 16px', background: '#edf2f7', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                disabled={applyConfirmInput !== 'Apply'}
                onClick={async () => {
                  await setDevDateOverride(devDateInput);
                  setDevDateOverrideState(devDateInput);
                  setShowApplyConfirm(false);
                  setApplyConfirmInput('');
                }}
                style={{
                  padding: '8px 16px',
                  background: applyConfirmInput === 'Apply' ? '#c53030' : '#fed7d7',
                  color: '#fff', border: 'none', borderRadius: '6px',
                  fontSize: '14px', fontWeight: '600',
                  cursor: applyConfirmInput === 'Apply' ? 'pointer' : 'not-allowed',
                }}
              >
                Apply Override
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
